import React, { useState, useEffect, useMemo } from "react";
import KC_Table from "../../../designsystem/KC_Table/KC_Table";
import { Pagination } from "react-bootstrap";
import KC_Button from "../../../designsystem/KC_Button/KC_Button";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { tt } from "../../../utils/i18n/i18n";
import { useLoadingSelector } from "../../../redux/actions/app";

interface PaginatedTableProps<T> {
  pageSize: number;
  totalCount: number;
  data: T[];
  onFetchCount: () => Promise<void>;
  onFetchData: (pageIndex: number) => Promise<void>;
  onDownload?: () => Promise<void>;
  renderHeaders: () => JSX.Element;
  renderRow: (item: T, index: number) => JSX.Element;
  keyPrefix: string;
  showDownloadButton?: boolean;
  trackingID: string;
}

export function PaginatedTable<T>(props: PaginatedTableProps<T>) {
  const showDownloadButton =
    props.showDownloadButton === undefined ? false : props.showDownloadButton;
  const [pageIndex, setPageIndex] = useState(0);
  const isLoading = useLoadingSelector();

  const pageCount = useMemo(() => {
    return Math.ceil(props.totalCount / props.pageSize);
  }, [props.totalCount, props.pageSize]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < pageCount) {
      setPageIndex(newPage);
    }
  };

  const renderPagination = () => {
    const PAGER_N_ITEMS = 2;

    const renderPageItems = () => {
      const items: JSX.Element[] = [];

      const lBound = Math.max(0, pageIndex - PAGER_N_ITEMS);
      const uBound = Math.min(pageIndex + PAGER_N_ITEMS, pageCount - 1);

      if (lBound > 0) {
        items.push(<Pagination.Ellipsis key="ellipsis-start" />);
      }

      for (let i = lBound; i <= uBound; i++) {
        items.push(
          <Pagination.Item
            key={`page-${i}`}
            active={i === pageIndex}
            onClick={() => handlePageChange(i)}
          >
            {i + 1}
          </Pagination.Item>
        );
      }

      if (uBound < pageCount - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-end" />);
      }

      return items;
    };

    return (
      <div className="d-flex justify-content-end align-items-center">
        <Pagination>
          <Pagination.First
            disabled={pageIndex === 0 || pageCount === 0}
            onClick={() => handlePageChange(0)}
          />
          <Pagination.Prev
            disabled={pageIndex === 0 || pageCount === 0}
            onClick={() => handlePageChange(pageIndex - 1)}
          />
          {renderPageItems()}
          <Pagination.Next
            disabled={pageIndex >= pageCount - 1 || pageCount === 0}
            onClick={() => handlePageChange(pageIndex + 1)}
          />
          <Pagination.Last
            disabled={pageIndex >= pageCount - 1 || pageCount === 0}
            onClick={() => handlePageChange(pageCount - 1)}
          />
        </Pagination>
      </div>
    );
  };

  useEffect(() => {
    setPageIndex(0);
    void props.onFetchCount();
  }, [props.onFetchCount, props.trackingID]);

  useEffect(() => {
    if (props.totalCount > 0) {
      void props.onFetchData(pageIndex);
    }
  }, [props.trackingID, pageIndex, props.totalCount, props.onFetchData]);

  if (props.totalCount === 0 && !isLoading) {
    return <div className="text-center p-4">{tt("no_data")}</div>;
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center st-gap mb-3">
        <div>
          {tt("total")}
          {props.totalCount}
        </div>
        <div className="d-flex justify-content-center align-items-center st-gap">
          {showDownloadButton && props.onDownload && (
            <KC_Button
              text={tt("download")}
              icon={faDownload}
              onClick={props.onDownload}
              disabled={isLoading || props.totalCount === 0}
              showIconOnTheLeft={true}
              className="ms-3"
              size="md"
            />
          )}
          {renderPagination()}
        </div>
      </div>
      <KC_Table
        keyPrefix={props.keyPrefix}
        headers={props.renderHeaders()}
        border={true}
      >
        {props.data.map((item, index) => props.renderRow(item, index))}
      </KC_Table>
      <div className="mt-3">{renderPagination()}</div>
    </>
  );
}
